import React from 'react';

import {
  CardContent,
  CardContentRow,
  CardContentRowButtons,
  CardContentRowContainer,
  CardContentRowItem,
  CardContentRowTextItems,
  CardListItem,
  LabelHolder,
  LabelsColumn
} from './InvoiceCardListItem.styled';
import { FilePdf } from 'assets/icons/Icons';
import { Button } from 'components/v2/Buttons/Button/Button';
import { Heading } from 'components/v2/Typography/Heading';
import { Text } from 'components/v2/Typography/Text';
import { currencyNumberFormat } from 'utilities/number-format';

import { CardContentItemLabel } from './CardContentItemLabel';
import { CardContentItemValue } from './CardContentItemValue';
import Invoice from '../../types/invoice';
import { formatDate } from '../PagableInvoices/PagableInvoices.utils';

interface Props {
  invoice: Invoice;
  hasUnappliedPaymentsTotalCents: boolean;
  onPayClick: () => void;
  onViewInvoice: () => void;
}

export const InvoiceCardListItem = ({
  invoice,
  hasUnappliedPaymentsTotalCents,
  onPayClick,
  onViewInvoice
}: Props) => {
  const { dateOfInvoice, service, balance, id, azEncounterIdentifier, paidViaPpApp } = invoice;

  const invoiceIdentifier = azEncounterIdentifier || id;
  const invoiceBalance = parseFloat(balance || '0');

  const showPaymentPlanInProgress = invoice.paymentPlan?.status === 'In Progress';

  const showPaidLabel = invoiceBalance === 0;
  const showProcessingLabel = paidViaPpApp && invoiceBalance > 0 && hasUnappliedPaymentsTotalCents;
  const showOutstandingLabel = !showPaymentPlanInProgress && !paidViaPpApp && invoiceBalance > 0;

  const showPayButton =
    (!paidViaPpApp && invoiceBalance > 0) ||
    (invoiceBalance > 0 && !hasUnappliedPaymentsTotalCents);

  const rowItems = [
    { label: 'invoice no.', value: invoiceIdentifier },
    { label: 'date', value: formatDate(dateOfInvoice) },
    { label: 'balance', value: currencyNumberFormat(invoiceBalance) }
  ];

  return (
    <CardListItem>
      <CardContent>
        <CardContentRow>
          <div>
            <CardContentItemLabel value="service" />
            <Heading tag="h3" noMargin>
              {service || '-'}
            </Heading>
          </div>
          <LabelsColumn>
            <Text tag="span" fontStyle="medium" size="sm">
              {showPaidLabel && <LabelHolder $status="paid">Paid</LabelHolder>}
              {showProcessingLabel && <LabelHolder $status="default">Processing...</LabelHolder>}
              {showOutstandingLabel && <LabelHolder $status="outstanding">Outstanding</LabelHolder>}
            </Text>
            <Text tag="span" fontStyle="medium" size="sm">
              {showPaymentPlanInProgress && (
                <LabelHolder $status="paid">Payment Plan in Progress</LabelHolder>
              )}
            </Text>
          </LabelsColumn>
        </CardContentRow>

        <CardContentRowContainer>
          <CardContentRowTextItems>
            {rowItems.map((item, index) => {
              return (
                <CardContentRowItem key={index}>
                  <CardContentItemLabel value={item.label} />
                  <CardContentItemValue value={item.value} />
                </CardContentRowItem>
              );
            })}
          </CardContentRowTextItems>

          <CardContentRowButtons>
            <Button
              label="View"
              category="secondary"
              size="md"
              fullWidth
              rightIcon={<FilePdf type="solid" />}
              onClick={onViewInvoice}
              uppercase
            />

            {showPayButton && (
              <Button
                label="Pay now"
                category="primary"
                size="md"
                fullWidth
                onClick={onPayClick}
                uppercase
              />
            )}
          </CardContentRowButtons>
        </CardContentRowContainer>
      </CardContent>
    </CardListItem>
  );
};
